import { validateServerMessage, fromUIMessage, type ThreadId, type UserMessage } from '@mesa/core/websocket/types';
import type { UIMessage } from 'ai';

export interface AiChatTransportOptions {
  url: string;
  threadId: ThreadId;
}

export class AiChatTransport {
  private ws: WebSocket | null = null;
  private messageCallbacks: Set<(message: UIMessage) => void> = new Set();
  private loadingCallbacks: Set<(isLoading: boolean) => void> = new Set();
  private connectionState: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
  private messageQueue: UserMessage[] = [];
  private pendingAssistantMessage: UIMessage | null = null;
  private assistantMessageContent = '';

  constructor(private options: AiChatTransportOptions) {}

  async connect(): Promise<void> {
    console.log('🔌 WebSocket Transport: Starting connection...');
    console.log('🌐 Connection URL:', this.options.url);
    console.log('🆔 Thread ID:', this.options.threadId);

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('✅ WebSocket already connected');
      return;
    }

    this.connectionState = 'connecting';
    console.log('🔄 Connection state changed to: connecting');

    return new Promise((resolve, reject) => {
      try {
        console.log('🚀 Creating new WebSocket connection...');
        this.ws = new WebSocket(this.options.url);

        this.ws.onopen = () => {
          console.log('🟢 WebSocket connection opened successfully!');
          this.connectionState = 'connected';

          console.log('📤 Sending queued messages:', this.messageQueue.length);
          // Send queued messages
          this.messageQueue.forEach((message) => {
            console.log('📨 Sending queued message:', message);
            this.ws!.send(JSON.stringify(message));
          });
          this.messageQueue = [];

          resolve();
        };

        this.ws.onmessage = (event) => {
          console.log('📥 WebSocket message received:', event.data);
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          console.log('🔴 WebSocket connection closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });
          this.connectionState = 'disconnected';
          // TODO: Implement reconnection logic
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket connection error:', error);
          this.connectionState = 'error';
          reject(error);
        };
      } catch (error) {
        console.error('💥 Failed to create WebSocket:', error);
        this.connectionState = 'error';
        reject(error);
      }
    });
  }

  private handleMessage(data: string) {
    try {
      const serverMessage = validateServerMessage(JSON.parse(data));

      if (serverMessage.type === 'user_message' || serverMessage.type === 'assistant_message') {
        // Handle complete message (user or assistant)
        const textParts = serverMessage.parts
          .filter((p) => p.type === 'text')
          .map((p) => ({ type: 'text' as const, text: p.text }));

        const uiMessage: UIMessage = {
          id: serverMessage.id,
          role: serverMessage.role,
          parts: textParts,
          metadata: { timestamp: serverMessage.timestamp },
        };

        this.messageCallbacks.forEach((callback) => callback(uiMessage));
      } else if (serverMessage.type === 'assistant_message_start') {
        // Start building assistant message and set loading state
        this.pendingAssistantMessage = {
          id: serverMessage.messageId,
          role: 'assistant',
          parts: [], // Start empty, will be filled by deltas
          metadata: { timestamp: serverMessage.timestamp },
        };
        this.assistantMessageContent = '';

        // Notify that AI is loading
        this.loadingCallbacks.forEach((callback) => callback(true));

        this.messageCallbacks.forEach((callback) => callback(this.pendingAssistantMessage!));
      } else if (serverMessage.type === 'assistant_message_delta') {
        // Update assistant message with delta
        if (this.pendingAssistantMessage) {
          this.assistantMessageContent += serverMessage.delta.text;

          // First delta means AI started responding - stop loading
          if (this.assistantMessageContent.length === serverMessage.delta.text.length) {
            this.loadingCallbacks.forEach((callback) => callback(false));
          }

          const updatedMessage: UIMessage = {
            ...this.pendingAssistantMessage,
            parts: [{ type: 'text' as const, text: this.assistantMessageContent }],
          };

          this.messageCallbacks.forEach((callback) => callback(updatedMessage));
        }
      } else if (serverMessage.type === 'assistant_message_complete') {
        // Finalize assistant message and stop loading
        if (this.pendingAssistantMessage) {
          const finalMessage: UIMessage = {
            ...this.pendingAssistantMessage,
            parts: [{ type: 'text' as const, text: this.assistantMessageContent }],
          };

          this.messageCallbacks.forEach((callback) => callback(finalMessage));
          this.pendingAssistantMessage = null;
          this.assistantMessageContent = '';
        }

        // Ensure loading is stopped
        this.loadingCallbacks.forEach((callback) => callback(false));
      } else if (serverMessage.type === 'error') {
        console.error('WebSocket error message:', serverMessage.message);
        // Stop loading on error
        this.loadingCallbacks.forEach((callback) => callback(false));
      }
      // Note: Removed user_typing handler as it's not used in UI yet
    } catch (error) {
      console.error('Failed to handle WebSocket message:', error);
    }
  }

  async sendMessage(message: UIMessage): Promise<void> {
    const wsMessage: UserMessage = fromUIMessage(message, this.options.threadId);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(wsMessage));
    } else {
      // Queue message if not connected
      this.messageQueue.push(wsMessage);

      // Try to connect if not already connecting
      if (this.connectionState === 'disconnected') {
        await this.connect();
      }
    }
  }

  onMessage(callback: (message: UIMessage) => void): () => void {
    this.messageCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.messageCallbacks.delete(callback);
    };
  }

  onLoadingChange(callback: (isLoading: boolean) => void): () => void {
    this.loadingCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.loadingCallbacks.delete(callback);
    };
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectionState = 'disconnected';
    this.messageCallbacks.clear();
    this.loadingCallbacks.clear();
  }

  // Typing indicators
  startTyping(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'typing_start',
          threadId: this.options.threadId,
        })
      );
    }
  }

  stopTyping(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'typing_stop',
          threadId: this.options.threadId,
        })
      );
    }
  }

  getConnectionState() {
    return this.connectionState;
  }
}

// Simplified factory function
export const createAiChatTransport = (options: AiChatTransportOptions) => new AiChatTransport(options);
